import { useState, useEffect } from 'react';
import BgNsxAddressSelector from '@britishgasenergy/react-nucleus/dist/nuc-components/NsxAddressSelector';
import NsContent from 'components/common/ns-content/NsContent';
import { shallowEqual } from 'react-redux';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

import { useMaskPII } from 'hooks/useMaskPII';
import {
  setInitialState as resetSelectedAddress,
  setSelectedAddress,
  selectedAddress as selectCurrentAddress
} from 'state/selected-address/selected-address-slice';
import { fromNucleusAddress } from 'utils/nucAddressFormat';
import {
  setInitialState as resetAddressList,
  setAddressList
} from 'state/address-list/address-list-slice';
import { getAddressListThunk } from 'state/address-list/address-list-thunks';
import { setInitialState as resetMeterType } from 'state/meter-type/meter-type-slice';
import { setInitialState as resetInitiate } from 'state/initiate/initiate-slice';
import { initiateThunk } from 'state/initiate/initiate-thunks';
import {
  setPostcode,
  setInitialState as resetPostcode
} from 'state/postcode/postcode-slice';
import { useNsxAddressSelector } from './hooks/useNsxAddressSelector';
import { NoAddressFound } from 'components/common/nsx-address-selector/NoAddressFound';
import { Error } from 'components/common/error/Error';
import { getMeterTypeApiThunk } from 'state/meter-type/meter-type-thunks';
import { AddressSelectorSkeleton } from 'components/common/nsx-address-selector/AddressSelectorSkeleton';
import { NsPrimaryCta } from 'components/common/ns-primary-cta/NsPrimaryCta';

import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';

export const testId = 'nsxAddressSelectorTestId';

type NsxAddressSelectorProps = {
  handleGoNext?(): void;
  fetchMeterDetails?: boolean;
  defaultPostcode?: string | null;
};

export const NsxAddressSelector = ({
  handleGoNext: handleGoNextCallback,
  fetchMeterDetails = true,
  defaultPostcode
}: NsxAddressSelectorProps) => {
  const [key, setKey] = useState(uuidv4());
  const [eventFired, setEventFired] = useState(false);
  const [showSkeleton, setShowSkeleton] = useState(false);
  const selectedAddress = useAppSelector(selectCurrentAddress, shallowEqual);
  const dispatch = useAppDispatch();
  const { mask } = useMaskPII();

  const {
    noAddressFound,
    isSingleAddress,
    postcode,
    nucAddresses,
    errors
  } = useNsxAddressSelector();

  const onPostcodeSelected = async (event) => {
    const {
      detail: { postcode }
    } = event;
    const source = axios.CancelToken.source();
    const requestConfig = { cancelToken: source.token };

    dispatch(setPostcode(postcode));

    const callApis = async () => {
      dispatch(resetAddressList());

      try {
        await dispatch(initiateThunk(requestConfig));

        const response = await dispatch(
          getAddressListThunk(postcode, requestConfig)
        );

        const noAddressFound = !response.addresses.length;
        const isSingleAddress = response.addresses.length === 1;

        if (noAddressFound) {
          dispatch(setAddressList([]));
        }

        if (isSingleAddress) {
          dispatch(setSelectedAddress(response.addresses[0]));
        }
      } catch (e) {
        dispatch(setAddressList([]));
        e && console.error(e);
      } finally {
        setShowSkeleton(false);
      }
    };

    return await callApis();
  };

  const onChangePostcodeClick = () => {
    // prevent nsx-address-selector from re-rendering during reset and firing
    // events multiple times
    setEventFired(true);

    dispatch(resetInitiate());
    dispatch(resetPostcode());
    dispatch(resetAddressList());
    dispatch(resetSelectedAddress());
    dispatch(resetMeterType());

    // force nsx-address-selector to re-render. bug in nucleus
    setKey(uuidv4());
    setEventFired(false);
  };

  const handleGoNext = async (currentAddress) => {
    try {
      if (fetchMeterDetails) {
        await dispatch(getMeterTypeApiThunk(currentAddress?.udprn));
      }
      handleGoNextCallback && handleGoNextCallback();
    } catch (e) {
      e && console.error(e);
    }
  };

  const onAddressSelected = async (event) => {
    const {
      detail: { address }
    } = event;

    const isChangePostcodeClick = !address; // means 'change postcode' clicked

    if (isChangePostcodeClick) {
      return onChangePostcodeClick();
    }

    if (!isSingleAddress) {
      const currentAddress = fromNucleusAddress(address);
      dispatch(setSelectedAddress(currentAddress));
      await handleGoNext(currentAddress);
    }
  };

  const chooseSingleAddressClick = () => {
    handleGoNext(selectedAddress);
  };

  useEffect(() => {
    if (defaultPostcode) {
      setShowSkeleton(true);
      onPostcodeSelected({ detail: { postcode: defaultPostcode } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <NsContent data-testid={testId}>
      {showSkeleton ? (
        <AddressSelectorSkeleton postcode={postcode} />
      ) : (
        <>
          {!eventFired && (
            <BgNsxAddressSelector
              key={key}
              name="address"
              onPostcodeSelected={onPostcodeSelected}
              onAddressSelected={onAddressSelected}
              addresses={nucAddresses ?? ''}
              postcode={postcode}
              required
              {...mask}
            />
          )}
          {isSingleAddress && (
            <NsPrimaryCta onClick={chooseSingleAddressClick}>
              Choose this address
            </NsPrimaryCta>
          )}
        </>
      )}
      <Error errors={errors} />

      {noAddressFound && <NoAddressFound />}
    </NsContent>
  );
};
